import { Service } from '@angular/core';
import { inject } from '@angular/core';
// The AWS Cognito SDKs (`@aws-sdk/client-cognito-identity-provider` + `amazon-cognito-identity-js`) are
// the app's single heaviest dependency and are needed ONLY for the login / refresh / password / MFA
// flows — never on the common startup session-restore path (which decodes the stored token with `jose`
// below). They are therefore loaded via dynamic import() (see loadAwsCognito / loadCognitoIdentity) and
// esbuild splits them into their own lazy chunks, keeping them out of the initial bundle (WS-2 P3).
// Bindings used purely as types are `import type` (erased at compile — no runtime / bundle cost).
import type {
  AttributeType,
  AuthenticationResultType,
  ChangePasswordCommandOutput,
  CognitoIdentityProviderClient,
  GetUserCommandOutput,
  InitiateAuthCommandOutput,
} from '@aws-sdk/client-cognito-identity-provider';
import type { CognitoUser, CognitoUserPool, CognitoUserSession } from 'amazon-cognito-identity-js';
import { defer, finalize, from, Observable, shareReplay, switchMap, throwError } from 'rxjs';
// `jose.decodeJwt` is a tiny base64url decode (no crypto) needed synchronously during startup
// session-restore, so it stays a static import — it tree-shakes to almost nothing.
import { decodeJwt } from 'jose';
import { ConfigService } from '../services/config';
import { AuthUser } from '../models/user.model';
import { isRole } from '../enums/role.enum';
import { AUTH_TOKEN_KEYS, REDIRECT_URL_KEY } from '../constants/auth';
import { text } from './wire-utils';

/** Lazily-loaded module shapes for the dynamically-imported Cognito SDKs. */
type AwsCognitoModule = typeof import('@aws-sdk/client-cognito-identity-provider');
type CognitoIdentityModule = typeof import('amazon-cognito-identity-js');

/** One-time dynamic imports so each heavy Cognito SDK loads lazily, once, on first use. */
let awsCognitoPromise: Promise<AwsCognitoModule> | null = null;
const loadAwsCognito = (): Promise<AwsCognitoModule> =>
  (awsCognitoPromise ??= import('@aws-sdk/client-cognito-identity-provider'));

let cognitoIdentityPromise: Promise<CognitoIdentityModule> | null = null;
const loadCognitoIdentity = (): Promise<CognitoIdentityModule> =>
  (cognitoIdentityPromise ??= import('amazon-cognito-identity-js'));

/** Module-level singleton — ensures concurrent 401s share one refresh call across all instances. */
let refreshInFlight$: Observable<InitiateAuthCommandOutput> | null = null;

/**
 * The CognitoUser mid-challenge (SRP keeps challenge state on the in-memory user object, not a
 * portable Session token), so NEW_PASSWORD/MFA completion must reuse the same instance.
 */
let pendingChallengeUser: CognitoUser | null = null;

/** Cognito challenge names the login flow surfaces to the effects/UI. */
export type AuthChallenge = 'NEW_PASSWORD_REQUIRED' | 'SOFTWARE_TOKEN_MFA' | 'MFA_SETUP' | 'SMS_MFA';

/** JWT `amr` values that mean a second factor was used (mirrors vantage-backend's marker set). */
const MFA_AMR_MARKERS = new Set(['mfa', 'software_token_mfa', 'sms_mfa', 'otp']);

/**
 * Normalized auth result the effects consume — mirrors the shape the AWS-SDK flow returned, so
 * the event/effect orchestration is unchanged. Either a challenge is pending, or tokens issued.
 */
export interface AuthResult {
  ChallengeName?: AuthChallenge;
  /** Unused under SRP (challenge state lives on `pendingChallengeUser`); kept for shape parity. */
  Session?: string;
  AuthenticationResult?: { AccessToken: string; IdToken: string; RefreshToken: string };
}

/** CognitoUserSession → the normalized token shape `saveSession`/effects expect. */
function toAuthResult(session: CognitoUserSession): AuthResult {
  return {
    AuthenticationResult: {
      AccessToken: session.getAccessToken().getJwtToken(),
      IdToken: session.getIdToken().getJwtToken(),
      RefreshToken: session.getRefreshToken().getToken(),
    },
  };
}

@Service()
export class AuthDataService {
  readonly #config = inject(ConfigService);

  /** Built after the AWS SDK chunk loads (dynamic import), then memoized — NOT at construction, so
   *  bootstrapping the root store never pulls the SDK into the initial bundle (WS-2 P3). */
  #client: CognitoIdentityProviderClient | null = null;
  /** Lazily built so tests/SSR that never sign in don't trip CognitoUserPool id validation. */
  #pool: CognitoUserPool | null = null;

  /** Load the AWS SDK chunk (once) + memoize the Cognito client. */
  async #aws(): Promise<{ aws: AwsCognitoModule; client: CognitoIdentityProviderClient }> {
    const aws = await loadAwsCognito();
    this.#client ??= new aws.CognitoIdentityProviderClient({ region: this.#config.cognitoRegion });
    return { aws, client: this.#client };
  }

  /** Load the amazon-cognito-identity-js chunk (once) + memoize the user pool. */
  async #cognito(): Promise<{ cognito: CognitoIdentityModule; pool: CognitoUserPool }> {
    const cognito = await loadCognitoIdentity();
    this.#pool ??= new cognito.CognitoUserPool({
      UserPoolId: this.#config.cognitoUserPoolId,
      ClientId: this.#config.cognitoClientId,
    });
    return { cognito, pool: this.#pool };
  }

  // ─── Auth flows ────────────────────────────────────────────────────────────

  /**
   * Sign in via **SRP** (`USER_SRP_AUTH`) — the dev/qa app clients disable `USER_PASSWORD_AUTH`,
   * and SRP never sends the raw password. Resolves to issued tokens or a pending challenge
   * (NEW_PASSWORD_REQUIRED / SOFTWARE_TOKEN_MFA / MFA_SETUP); the CognitoUser is retained for
   * challenge completion.
   */
  login(username: string, password: string): Observable<AuthResult> {
    return from(this.#cognito()).pipe(
      switchMap(({ cognito, pool }) =>
        new Observable<AuthResult>((subscriber) => {
          const user = new cognito.CognitoUser({ Username: username, Pool: pool });
          pendingChallengeUser = user;
          user.authenticateUser(
            new cognito.AuthenticationDetails({ Username: username, Password: password }),
            {
              onSuccess: (session) => {
                pendingChallengeUser = null;
                subscriber.next(toAuthResult(session));
                subscriber.complete();
              },
              onFailure: (err) => {
                pendingChallengeUser = null;
                subscriber.error(err);
              },
              newPasswordRequired: () => {
                subscriber.next({ ChallengeName: 'NEW_PASSWORD_REQUIRED' });
                subscriber.complete();
              },
              totpRequired: () => {
                subscriber.next({ ChallengeName: 'SOFTWARE_TOKEN_MFA' });
                subscriber.complete();
              },
              mfaSetup: () => {
                subscriber.next({ ChallengeName: 'MFA_SETUP' });
                subscriber.complete();
              },
              mfaRequired: () => {
                subscriber.next({ ChallengeName: 'SMS_MFA' });
                subscriber.complete();
              },
            },
          );
        }),
      ),
    );
  }

  /** Completes the SOFTWARE_TOKEN_MFA (TOTP) challenge using the retained CognitoUser. */
  respondToMfaChallenge(code: string): Observable<AuthResult> {
    return new Observable<AuthResult>((subscriber) => {
      const user = pendingChallengeUser;
      if (!user) {
        subscriber.error(new Error('Your sign-in session expired. Please sign in again.'));
        return;
      }
      user.sendMFACode(
        code,
        {
          onSuccess: (session) => {
            pendingChallengeUser = null;
            subscriber.next(toAuthResult(session));
            subscriber.complete();
          },
          onFailure: (err) => subscriber.error(err),
        },
        'SOFTWARE_TOKEN_MFA',
      );
    });
  }

  /**
   * Completes the NEW_PASSWORD_REQUIRED challenge. Admin-provisioned users start in
   * FORCE_CHANGE_PASSWORD, so their first sign-in must set a permanent password before tokens
   * issue. May chain into an MFA challenge (re-emitted here).
   */
  respondToNewPasswordChallenge(newPassword: string): Observable<AuthResult> {
    return new Observable<AuthResult>((subscriber) => {
      const user = pendingChallengeUser;
      if (!user) {
        subscriber.error(new Error('Your sign-in session expired. Please sign in again.'));
        return;
      }
      user.completeNewPasswordChallenge(newPassword, {}, {
        onSuccess: (session) => {
          pendingChallengeUser = null;
          subscriber.next(toAuthResult(session));
          subscriber.complete();
        },
        onFailure: (err) => subscriber.error(err),
        totpRequired: () => {
          subscriber.next({ ChallengeName: 'SOFTWARE_TOKEN_MFA' });
          subscriber.complete();
        },
        mfaSetup: () => {
          subscriber.next({ ChallengeName: 'MFA_SETUP' });
          subscriber.complete();
        },
      });
    });
  }

  getUser(accessToken: string): Observable<GetUserCommandOutput> {
    return from(this.#aws()).pipe(
      switchMap(({ aws, client }) => from(client.send(new aws.GetUserCommand({ AccessToken: accessToken })))),
    );
  }

  forgotPassword(email: string) {
    return from(this.#aws()).pipe(
      switchMap(({ aws, client }) =>
        from(client.send(new aws.ForgotPasswordCommand({ ClientId: this.#config.cognitoClientId, Username: email }))),
      ),
    );
  }

  confirmForgotPassword(email: string, code: string, newPassword: string) {
    return from(this.#aws()).pipe(
      switchMap(({ aws, client }) =>
        from(client.send(new aws.ConfirmForgotPasswordCommand({
          ClientId: this.#config.cognitoClientId,
          Username: email,
          ConfirmationCode: code,
          Password: newPassword,
        }))),
      ),
    );
  }

  /**
   * Self-service password change for the signed-in user via Cognito (`ChangePasswordCommand`) — there is
   * no REST `/auth/*` surface in v2. Uses the stored access token; Cognito enforces the current password
   * + the pool's password policy and surfaces a descriptive error (e.g. wrong current / weak new).
   */
  changePassword(currentPassword: string, newPassword: string): Observable<ChangePasswordCommandOutput> {
    const accessToken = localStorage.getItem(AUTH_TOKEN_KEYS.accessToken) ?? '';
    if (!accessToken) return throwError(() => new Error('Not signed in'));
    return from(this.#aws()).pipe(
      switchMap(({ aws, client }) =>
        from(client.send(new aws.ChangePasswordCommand({
          AccessToken: accessToken,
          PreviousPassword: currentPassword,
          ProposedPassword: newPassword,
        }))),
      ),
    );
  }

  /**
   * Refreshes the session using the stored refresh token.
   * Uses a shared observable so concurrent 401s only trigger one refresh call.
   */
  refreshSession(): Observable<InitiateAuthCommandOutput> {
    if (!refreshInFlight$) {
      refreshInFlight$ = defer(() => {
        const refreshToken = localStorage.getItem(AUTH_TOKEN_KEYS.refreshToken) ?? '';
        if (!refreshToken) {
          return throwError(() => new Error('No refresh token stored'));
        }
        return from(this.#aws()).pipe(
          switchMap(({ aws, client }) =>
            from(client.send(new aws.InitiateAuthCommand({
              ClientId: this.#config.cognitoClientId,
              AuthFlow: aws.AuthFlowType.REFRESH_TOKEN_AUTH,
              AuthParameters: { REFRESH_TOKEN: refreshToken },
            }))),
          ),
        );
      }).pipe(
        finalize(() => {
          refreshInFlight$ = null;
        }),
        shareReplay({ bufferSize: 1, refCount: true }),
      );
    }
    return refreshInFlight$;
  }

  // ─── Token helpers ─────────────────────────────────────────────────────────

  /**
   * Persists Cognito tokens to localStorage.
   * Preserves existing RefreshToken when a new one is not returned (REFRESH_TOKEN_AUTH flow).
   */
  saveSession(result: AuthenticationResultType): boolean {
    const { AccessToken: access, IdToken: id, RefreshToken: refresh } = result;
    if (!access || !id) return false;

    const storedRefresh =
      refresh ?? localStorage.getItem(AUTH_TOKEN_KEYS.refreshToken) ?? '';
    if (!storedRefresh) return false;

    localStorage.setItem(AUTH_TOKEN_KEYS.accessToken, access);
    localStorage.setItem(AUTH_TOKEN_KEYS.idToken, id);
    localStorage.setItem(AUTH_TOKEN_KEYS.refreshToken, storedRefresh);
    return true;
  }

  clearSession(): void {
    localStorage.removeItem(AUTH_TOKEN_KEYS.idToken);
    localStorage.removeItem(AUTH_TOKEN_KEYS.accessToken);
    localStorage.removeItem(AUTH_TOKEN_KEYS.refreshToken);
    localStorage.removeItem(REDIRECT_URL_KEY);
  }

  /** Returns false if the JWT is expired or unparseable. */
  isTokenValid(token: string): boolean {
    try {
      const { exp } = decodeJwt(token);
      return exp != null && Date.now() < exp * 1000;
    } catch {
      return false;
    }
  }

  // ─── Attribute mapping ─────────────────────────────────────────────────────

  /**
   * Builds the session identity by decoding the idToken JWT. The Cognito
   * `pre_token_generation` trigger injects the 9 Vantage claims (portal, role, entity_id,
   * territory_ids, managed_account_ids, sub_proxy_terr_ids, mfa_required, user_id) into the
   * token, so this needs no extra network call and works before `/portal/me` is wired.
   * Defensive by design — a missing/odd claim yields empty values rather than throwing,
   * so an unexpected token shape never locks a user out.
   */
  sessionFromIdToken(idToken: string): AuthUser {
    let claims: Record<string, unknown> = {};
    try {
      claims = decodeJwt(idToken) as Record<string, unknown>;
    } catch {
      /* malformed token — fall through to empty session */
    }

    const str = (k: string): string => (typeof claims[k] === 'string' ? (claims[k] as string) : '');
    const csv = (k: string): string[] =>
      str(k)
        .split(',')
        .map((s) => s.trim())
        .filter(Boolean);

    // ⚠️ Three of the claims parsed below are NOT minted by `pre_token_generation` — it emits
    // exactly `custom:user_id`, `custom:portal`, `custom:role`, `custom:entity_id` and
    // `custom:mfa_required`. `custom:territory_ids`, `custom:managed_account_ids` and
    // `custom:sub_proxy_terr_ids` therefore always parse to `[]` against a real token.
    //
    // They are parsed rather than removed so a future token that DOES carry them works without a
    // change here — but nothing may depend on them being populated. Tenancy is per-request under
    // RLS by design (the deliberately thin token; baking it is the RBP #22 trap).
    const territoryIds = csv('custom:territory_ids');

    // The ONE place the role claim crosses into the app. It is deliberately not rejected here —
    // this parser's contract is "a missing/odd claim yields empty values rather than throwing" and
    // every caller depends on that — but an unrecognised value is now SAID, loudly, instead of
    // becoming an empty capability set and a silent bounce to /auth/login (the login effect turns
    // it into the same failure a missing claim already produced). `Role` is the enumerator; see
    // `isRole`'s TSDoc for why this is unreachable today and reachable the next time a role is
    // renamed.
    const roleClaim = str('custom:role');
    if (roleClaim && !isRole(roleClaim)) {
      console.error(
        `[auth] UNRECOGNISED_ROLE_CLAIM: custom:role="${roleClaim}" is not a member of the Role ` +
          'enum (libs/core-data/src/lib/enums/role.enum.ts), which mirrors the seeded backend role ' +
          'table. This identity holds NO capabilities and cannot be landed in any portal area — ' +
          'either the backend renamed a role without the web following, or the token is not ours.',
      );
    }

    const fullName = str('name') || `${str('given_name')} ${str('family_name')}`.trim();
    const [first, ...rest] = fullName ? fullName.split(/\s+/) : [''];

    return {
      sub: str('sub'),
      email: str('email'),
      givenName: str('given_name') || first || '',
      familyName: str('family_name') || rest.join(' '),
      emailVerified: text(claims['email_verified']),
      'custom:role': roleClaim,
      'custom:territory_id': str('custom:territory_id') || territoryIds[0] || '',
      portal: str('custom:portal'),
      territoryIds,
      managedAccountIds: csv('custom:managed_account_ids'),
      subProxyTerritoryIds: csv('custom:sub_proxy_terr_ids'),
      mfaRequired: str('custom:mfa_required') === 'true',
      userId: str('custom:user_id'),
    };
  }

  /**
   * True if the stored token shows a second factor was used (`amr`). Internal staff whose token
   * is required-but-unsatisfied are 403'd by the backend until they enroll TOTP. Cognito carries
   * `amr` on the **ID token** (also the token the backend reads / we send as Bearer), so check it
   * first; fall back to the access token defensively.
   */
  tokenHasMfa(): boolean {
    const hasMarker = (key: string): boolean => {
      try {
        const t = localStorage.getItem(key);
        if (!t) return false;
        const { amr } = decodeJwt(t) as { amr?: unknown };
        return Array.isArray(amr) && amr.some((a) => typeof a === 'string' && MFA_AMR_MARKERS.has(a));
      } catch {
        return false;
      }
    };
    return hasMarker(AUTH_TOKEN_KEYS.idToken) || hasMarker(AUTH_TOKEN_KEYS.accessToken);
  }

  // ─── TOTP MFA enrollment (Cognito challenge flow — see vantage-backend get_auth) ─────

  /**
   * Begins TOTP enrollment: associates a software token on the current (just-authenticated)
   * Cognito user and returns the base32 secret to show as a QR/manual key.
   */
  startMfaSetup(): Observable<string> {
    return from(this.#cognito()).pipe(
      switchMap(({ pool }) =>
        new Observable<string>((subscriber) => {
          const user = pool.getCurrentUser();
          if (!user) {
            subscriber.error(new Error('Your session expired. Please sign in again.'));
            return;
          }
          user.getSession((err: Error | null) => {
            if (err) {
              subscriber.error(err);
              return;
            }
            user.associateSoftwareToken({
              associateSecretCode: (secretCode) => {
                subscriber.next(secretCode);
                subscriber.complete();
              },
              onFailure: (e) => subscriber.error(e),
            });
          });
        }),
      ),
    );
  }

  /**
   * Verifies the user's first TOTP code and makes the authenticator their preferred MFA. After
   * this they must sign in again so Cognito issues a token carrying the MFA `amr`.
   */
  verifyMfaSetup(code: string): Observable<void> {
    return from(this.#cognito()).pipe(
      switchMap(({ pool }) =>
        new Observable<void>((subscriber) => {
          const user = pool.getCurrentUser();
          if (!user) {
            subscriber.error(new Error('Your session expired. Please sign in again.'));
            return;
          }
          user.getSession((err: Error | null) => {
            if (err) {
              subscriber.error(err);
              return;
            }
            user.verifySoftwareToken(code, 'Vantage Authenticator', {
              onSuccess: () => {
                user.setUserMfaPreference(null, { PreferredMfa: true, Enabled: true }, (e) => {
                  if (e) {
                    subscriber.error(e);
                  } else {
                    subscriber.next();
                    subscriber.complete();
                  }
                });
              },
              onFailure: (e) => subscriber.error(e),
            });
          });
        }),
      ),
    );
  }

  /** Maps Cognito GetUser UserAttributes array to a typed AuthUser object. */
  authUserFromAttrs(attrs: AttributeType[]): AuthUser {
    const user: AuthUser = {
      sub: '',
      email: '',
      givenName: '',
      familyName: '',
      emailVerified: '',
      'custom:role': '',
      'custom:territory_id': '',
    };

    for (const { Name, Value } of attrs) {
      if (!Name) continue;
      switch (Name) {
        case 'sub':                     user.sub = Value ?? ''; break;
        case 'email':                   user.email = Value ?? ''; break;
        case 'given_name':              user.givenName = Value ?? ''; break;
        case 'family_name':             user.familyName = Value ?? ''; break;
        case 'email_verified':          user.emailVerified = Value ?? ''; break;
        case 'custom:role':             user['custom:role'] = Value ?? ''; break;
        case 'custom:territory_id':     user['custom:territory_id'] = Value ?? ''; break;
      }
    }

    return user;
  }
}
